import PageHeader from "../Components/PageHeader";
import Section from "../Components/Section";
import Card from "../Components/Card";
import { FaRegLightbulb, FaRegHandshake } from "react-icons/fa6";
import { LuSettings } from "react-icons/lu";
import { FaPeopleGroup } from "react-icons/fa6";
import useChangeTitle from "../Hooks/useChangeTitle.jsx";

export default function HVAC() {
  useChangeTitle("HVAC Services");

  const hvacFeatures = [
    {
      index: "1",
      title: "Energy Efficiency",
      desc: "Our HVAC systems are designed for maximum energy efficiency, reducing operational costs while maintaining optimal comfort.",
      icon: <FaRegLightbulb />,
    },
    {
      index: "2", 
      title: "Professional Installation",
      desc: "Expert installation by certified technicians following international standards and best practices.",
      icon: <FaRegHandshake />,
    },
    {
      index: "3",
      title: "System Integration", 
      desc: "Seamless integration of VAV, CAV, VRF, CCD, FCU, RTU, and Package Units for optimal performance.",
      icon: <LuSettings />,
    },
    {
      index: "4",
      title: "24/7 Support",
      desc: "Comprehensive maintenance and support services to ensure your HVAC systems operate at peak efficiency.",
      icon: <FaPeopleGroup />,
    },
  ];

  return (
    <>
      <PageHeader heading="HVAC Systems" />
      
      {/* Hero Section with Video-like Background */}
      <div className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/home/hvac home.JPG" 
            alt="HVAC Installation" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-blue-900/90"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4 max-w-6xl mx-auto">
          <div className="animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Advanced
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
                HVAC Solutions
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-12 text-blue-100 max-w-4xl mx-auto leading-relaxed">
              Engineering climate perfection with cutting-edge heating, ventilation, and air conditioning 
              systems designed for optimal energy efficiency and indoor comfort.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300 shadow-2xl">
                Get HVAC Quote
              </button>
              <button className="border-2 border-white text-white hover:bg-white hover:text-blue-800 px-10 py-4 rounded-full font-semibold text-lg transform hover:scale-105 transition-all duration-300">
                View Projects
              </button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent"></div>
      </div>

      {/* Technical Standards Section */}
      <div className="py-24 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-32 h-32 border border-blue-400 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-24 h-24 border border-cyan-400 rounded-full opacity-30"></div>
          <div className="absolute top-1/2 left-1/3 w-16 h-16 bg-blue-500 rounded-full opacity-10"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-4xl font-bold mb-8">Industry Standards Excellence</h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              All our HVAC installations comply with the most stringent international standards, 
              ensuring safety, efficiency, and reliability.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10">
            {[
              { name: "ASHRAE", desc: "American Society of Heating Standards" },
              { name: "SMACNA", desc: "Sheet Metal Air Conditioning Guidelines" },
              { name: "ARI", desc: "Air Conditioning & Refrigeration Institute" },
              { name: "ASME", desc: "American Society of Mechanical Engineers" }
            ].map((standard, index) => (
              <div key={index} className="text-center group">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-all duration-500 transform hover:scale-105">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:rotate-12 transition-transform duration-300">
                    <span className="text-white font-bold text-lg">{standard.name}</span>
                  </div>
                  <h3 className="font-bold text-lg mb-3 text-white">{standard.name}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{standard.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <Section 
        title="Why Choose Our HVAC Services" 
        desc="Our comprehensive HVAC solutions combine cutting-edge technology with expert installation and maintenance services."
        className="bg-white"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {hvacFeatures.map((feature) => (
            <Card
              key={feature.index}
              index={feature.index}
              icon={feature.icon}
              title={feature.title}
              desc={feature.desc}
            />
          ))}
        </div>
      </Section>

      {/* Project Gallery */}
      <Section 
        title="Our HVAC Projects" 
        desc="Explore our portfolio of successful HVAC installations across various industries and building types."
        className="bg-gray-50"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          <div className="relative overflow-hidden rounded-xl shadow-lg group">
            <img 
              src="/images/home/hvac.jpg" 
              alt="Samsung Warehouses HVAC" 
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-6 left-6 text-white">
              <h4 className="font-semibold text-lg mb-2">Samsung Warehouses</h4>
              <p className="text-sm text-gray-200">Beni Sweef - HVAC Installation</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-xl shadow-lg group">
            <img 
              src="/images/services/hvac_231473096-1024x683.jpg" 
              alt="Commercial HVAC" 
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-6 left-6 text-white">
              <h4 className="font-semibold text-lg mb-2">Commercial Buildings</h4>
              <p className="text-sm text-gray-200">Advanced HVAC Solutions</p>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-xl shadow-lg group">
            <img 
              src="/images/home/hvac home.JPG" 
              alt="Industrial HVAC" 
              className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
            <div className="absolute bottom-6 left-6 text-white">
              <h4 className="font-semibold text-lg mb-2">Industrial Facilities</h4>
              <p className="text-sm text-gray-200">Large Scale HVAC Systems</p>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
} 